import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';

import { useBetslipStore } from '@/store/betslip';
import { useBookingButtonContext } from '../../contexts/booking-button-context';
import { Maybe, identity } from '@nf/utils-common/fpw/monadic';
import { useLocalStorage } from '@nf/utils-common/hooks';
import { uiService } from '@/services/ui.service';
import { useAccount } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useCheckReservationCount } from '@/hooks/query/reservation/use-check-reservation-count';

import { BookingIcon, CloseLineIcon } from '@/components/icons/svg-icons';
import { bookingButtonStyle } from './booking-button.style';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';

export const BookingButton = () => {
	const t = useTranslations('General');
	const { SiteConfigsData } = useGetSiteConfigs();
	const [isViewed, setIsViewed] = useLocalStorage('persist:is-bet-booking-new-tag-viewed', false);
	const [isInitialTipViewed, setIsInitialTipViewed] = useLocalStorage('persist:is-bet-booking-tip1-viewed', false);
	const isNewTagViewedRef = useRef(isViewed);
	const isInitialTipViewedRef = useRef(isInitialTipViewed);
	const [currentMultiTickets, isBookingBet, toggleBookingBet] = useBetslipStore(state => [
		state.currentMultiTickets,
		state.isBookingBet,
		state.toggleBookingBet
	]);
	const { isDisabled } = useBookingButtonContext();

	const singleTicket = currentMultiTickets[0];
	const { data: accountId } = useAccount(data => data.data.Data?.ID);
	const { data: reservationCount, isError } = useCheckReservationCount(accountId ?? 0, singleTicket.Matchid);

	// Handling of new tag
	useEffect(() => {
		!isViewed && setIsViewed(true);
	}, [isViewed]);

	// Handling of user's first-time opening
	useEffect(() => {
		!isInitialTipViewed && setIsInitialTipViewed(true);
		!isInitialTipViewedRef.current && uiService.displayBookingFeatureTip();
	}, [isInitialTipViewed]);

	const setKafka = (action :boolean) => {
		const kafkaData = {
			note: KafkaNote.EuroCup,
			type: 4,
			action: action ? 4 : 3,
		};
		setKafkaNormal(kafkaData);
	}

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		
		if (!isDisabled && !isError) {
			setKafka(isBookingBet);
			Maybe(reservationCount).fold(
				identity,
				(count: number) => {
					if (count < (SiteConfigsData?.ReservationMaxCount ?? 0)) {
						toggleBookingBet();
					} else {
						document.dispatchEvent(new CustomEvent('show-toast-msg', {
							detail: { msg: t('lbl_ECPBookingCheck') }
						}));
					}
				}
			);
		}
	};

	return (
		<button 
			className={clsx('btn btn--scheduled', bookingButtonStyle.bookedButton)}
			data-active={isBookingBet}
			data-disabled={isDisabled}
			onClick={handleClick}
		>			
			{isBookingBet ? <CloseLineIcon /> : <BookingIcon />}				
			<span className="btn__text">{t('lbl_ECPBooked')}</span>
			{!isNewTagViewedRef.current ? <div className="badge badge--new">NEW</div> : null}
		</button>
	);
};